import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Loader2, QrCode, AlertTriangle, PenLine, Search, Heading, Printer,
} from 'lucide-react';
import { PrintQueueCard } from '../components/PrintQueueCard';
import { PrintPreviewDialog } from '../components/PrintPreviewDialog';
import { PrioBadge } from '../components/PrioCriteriaPicker';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { QrReprintDialog } from '../components/QrReprintDialog';


const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const addrLines = (addr) => {
  const a = (addr || '').trim();
  if (!a) return [];
  if (a.includes('\n')) return a.split('\n').map(l => l.trim()).filter(Boolean);
  const parts = a.split(',').map(p => p.trim()).filter(Boolean);
  return parts.length > 1 ? parts : [a];
};


export default function ApprovalWorkplacePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [printRefresh, setPrintRefresh] = useState(0);
  const [previewLetter, setPreviewLetter] = useState(null);
  const [mailing, setMailing] = useState(null);
  const [sentSearch, setSentSearch] = useState('');
  const [qrSel, setQrSel] = useState({}); // letter_id → letter (survives search changes)
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const qrSelected = Object.values(qrSel);
  const hasQr = (l) => l.letter_kind === 'breakup' || !!l.landing;
  const toggleQr = (l) => setQrSel(s => { const n = { ...s }; if (n[l.id]) delete n[l.id]; else n[l.id] = l; return n; });
  const removeQr = (id) => setQrSel(s => { const n = { ...s }; delete n[id]; return n; });

  const setHeadline = async (letter, include) => {
    setBusyId(letter.id);
    try {
      await api.updateLetter(letter.id, { include_headline: include });
      toast.success(include
        ? `${letter.company_name}: Überschrift wird mitgeschrieben`
        : `${letter.company_name}: ohne Überschrift`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Überschrift konnte nicht umgestellt werden'); }
    finally { setBusyId(''); }
  };

  useEffect(() => {
    api.printMailingStatus().then(r => setMailing(r.data || {})).catch(() => {});
  }, [printRefresh]);

  const openPrintPreview = (letter, e) => {
    e.preventDefault();
    setPreviewLetter(letter);
  };

  const confirmWrite = async (letter, doc) => {
    const docType = doc || 'letter';
    setBusyId(letter.id);
    try {
      const r = await api.createPrintJob(letter.id, false, docType);
      toast.success(`${letter.company_name} queued for the iAuto handwriting machine (${r.data.pages} page${r.data.pages !== 1 ? 's' : ''})`);
      setPrintRefresh(n => n + 1);
      setPreviewLetter(null);
    } catch (err) {
      if (err.response?.status === 409) {
        toast('Dieses Dokument ist bereits in der Warteschlange.', {
          action: {
            label: 'Trotzdem schreiben',
            onClick: async () => {
              try {
                await api.createPrintJob(letter.id, true, docType);
                toast.success('Nochmal eingereiht');
                setPrintRefresh(n => n + 1);
                setPreviewLetter(null);
              } catch (e2) { toast.error(e2.response?.data?.detail || 'Failed'); }
            },
          },
        });
      } else {
        toast.error(err.response?.data?.detail || 'Could not queue the letter');
      }
    } finally { setBusyId(''); }
  };

  const load = () => {
    api.approvalWorkplace()
      .then(r => setData(r.data))
      .catch(err => {
        if (err.response?.status === 403) setForbidden(true);
        else toast.error('Could not load approval workplace');
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);



  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (forbidden) {
    return (
      <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm p-6 text-sm text-amber-300 max-w-xl" data-testid="approval-forbidden">
        Der iAuto Workspace ist nur für Admins, die Rolle „Produktion & Versand“ und Teammitglieder, die ein Admin unter Team → „iAuto: An“ freigeschaltet hat.
      </div>
    );
  }

  const pending = data?.pending || [];
  const approved = data?.approved || [];
  const sentRecent = data?.sent_recent || [];
  // Everything the iAuto can (re-)write: approved-but-unsent letters and recently sent ones —
  // searchable by company or owner (Talu, 31.08.2026). Break-up letters were abolished (04.11.2026).
  const seenIds = new Set();
  const rewritable = [...approved.filter(l => l.letter_type !== 'email'), ...sentRecent]
    .filter(l => !seenIds.has(l.id) && seenIds.add(l.id));
  const sq = sentSearch.trim().toLowerCase();
  const filteredRewritable = sq
    ? rewritable.filter(l => [l.company_name, l.project_name, l.owner?.name, l.recipient_name]
        .some(v => (v || '').toLowerCase().includes(sq)))
    : rewritable;

  const physicalApproved = approved.filter(l => l.letter_type !== 'email');
  const checksMap = mailing?.checks || {};
  const checkedRecipients = physicalApproved.filter(l => checksMap[l.id]?.all).length;

  const goNextRecipient = () => {
    const list = physicalApproved;
    if (!previewLetter || list.length === 0) { setPreviewLetter(null); return; }
    const idx = list.findIndex(l => l.id === previewLetter.id);
    if (idx === -1) { setPreviewLetter(null); return; }
    for (let i = 1; i <= list.length; i++) {
      const cand = list[(idx + i) % list.length];
      if (cand.id !== previewLetter.id && !checksMap[cand.id]?.all) { setPreviewLetter(cand); return; }
    }
    setPreviewLetter(null);
    toast.success('Alle Empfänger geprüft 🎉');
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="approval-workplace-page">
      <div>
        <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2">
          <Printer className="w-6 h-6 text-emerald-400" strokeWidth={1.5} /> iAuto Workspace
        </h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="iauto-workspace-subtitle">
          {approved.length} freigegebene Briefe in der Produktion
          {pending.length > 0 ? ` · ${pending.length} noch beim Brief-Approver in Prüfung` : ''} — 50er-Serien mit Datum, Nachschreiben. Freigaben laufen über die Prio-Rollen; „Als versendet markieren" findest du unter Post.
        </p>
      </div>

      {/* Re-write & re-adjust: any written letter — sent or not — searchable by company/owner */}
      <CollapsibleSection
        storageKey="approvals.rewrite"
        icon={PenLine}
        iconClass="text-purple-400"
        title="Nachschreiben & Nachjustieren — Brief, Flyer, Umschlag"
        count={rewritable.length}
        testId="approval-rewrite-section"
      >
        {rewritable.length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted-foreground">Keine schreibbaren Dokumente — weder freigegebene noch kürzlich versendete Briefe.</p>
        ) : (
          <div className="divide-y divide-border">
            <div className="px-4 py-2 flex items-center gap-2 sticky top-0 z-10 bg-card">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <input type="text" value={sentSearch} onChange={e => setSentSearch(e.target.value)}
                placeholder="Nach Unternehmen oder Unternehmer suchen…"
                data-testid="approval-rewrite-search-input"
                className="w-full bg-transparent text-sm sm:text-xs text-foreground placeholder:text-muted-foreground focus:outline-none py-1.5" />
              {sq && (
                <button onClick={() => setSentSearch('')} data-testid="approval-rewrite-search-clear"
                  className="text-xs text-muted-foreground hover:text-foreground shrink-0 px-2 py-1">✕</button>
              )}
              {sq && <span className="text-[10px] text-muted-foreground font-mono shrink-0">{filteredRewritable.length}/{rewritable.length}</span>}
            </div>
            <p className="px-4 py-2 text-[10px] text-muted-foreground">
              Auch für noch nicht versendete Briefe — nichts mehr löschen! Suchen → Vorschau öffnen → bei Bedarf nachjustieren → Brief, Flyer oder Umschlag einzeln neu an die iAuto schicken. Der Brief-Status bleibt unverändert.
              {' '}QR-Overlay falsch justiert? Firmen links anhaken → „QR-Overlay neu drucken“ — mit korrigiertem Versatz, nur für die Auswahl.
            </p>
            {qrSelected.length > 0 && (
              <div className="px-4 py-2 flex items-center gap-2 flex-wrap bg-sky-500/5 sticky top-9 z-10" data-testid="approval-qr-selection-bar">
                <QrCode className="w-3.5 h-3.5 text-sky-400" />
                <span className="text-[11px] text-foreground" data-testid="approval-qr-selection-count">{qrSelected.length} für QR-Nachdruck ausgewählt</span>
                <span className="text-[10px] text-muted-foreground truncate max-w-[50%]">{qrSelected.slice(0, 4).map(l => l.company_name).join(' · ')}{qrSelected.length > 4 ? ' …' : ''}</span>
                <span className="ml-auto flex items-center gap-2">
                  <button onClick={() => setQrSel({})} data-testid="approval-qr-selection-clear"
                    className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2">Auswahl leeren</button>
                  <Button size="sm" onClick={() => setQrDialogOpen(true)} data-testid="approval-qr-reprint-btn"
                    className="h-7 text-[11px] rounded-sm bg-sky-600 hover:bg-sky-500 text-white">
                    <QrCode className="w-3 h-3" /> QR-Overlay neu drucken ({qrSelected.length})…
                  </Button>
                </span>
              </div>
            )}
            {filteredRewritable.length === 0 && (
              <p className="px-4 py-4 text-xs text-muted-foreground" data-testid="approval-rewrite-search-empty">Kein Treffer für „{sentSearch}".</p>
            )}
            {filteredRewritable.slice(0, sq ? 50 : 10).map(l => (
              <div key={l.id} className={`px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 hover:bg-secondary/20 transition-colors ${qrSel[l.id] ? 'bg-sky-500/5' : ''}`}
                data-testid={`approval-rewrite-row-${l.id}`}>
                <input type="checkbox" checked={!!qrSel[l.id]} disabled={!hasQr(l)} onChange={() => toggleQr(l)}
                  data-testid={`approval-rewrite-qr-check-${l.id}`} className="accent-sky-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                  title={hasQr(l) ? 'Für QR-Overlay-Nachdruck auswählen' : 'Ohne Landing Page — kein QR-Code'} />
                <span className="text-xs font-medium text-foreground">{l.company_name}</span>
                <PrioBadge prio={l.prio} testid={`approval-rewrite-prio-${l.id}`} />
                {(l.owner?.name || l.recipient_name) && <span className="text-[11px] text-muted-foreground">→ {l.owner?.name || l.recipient_name}</span>}
                {l.letter_kind === 'breakup' && (
                  <span className="text-[9px] font-mono font-bold text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-1.5 py-0.5"
                    data-testid={`approval-rewrite-breakup-badge-${l.id}`}>BREAK-UP</span>
                )}
                {l.sent_at ? (
                  <span className="text-[10px] text-muted-foreground">versendet {fmtDate(l.sent_at)}</span>
                ) : (
                  <span className="text-[9px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5"
                    data-testid={`approval-rewrite-unsent-badge-${l.id}`}>noch nicht versendet</span>
                )}
                <span className="flex flex-wrap items-center gap-2 text-[10px] w-full sm:w-auto sm:ml-auto">
                  <button onClick={(e) => { e.preventDefault(); setHeadline(l, !l.include_headline); }}
                    disabled={busyId === l.id}
                    data-testid={`approval-rewrite-headline-chip-${l.id}`}
                    title={l.include_headline
                      ? 'Überschrift wird geschrieben — klicken zum Abschalten'
                      : 'Ohne Überschrift — klicken zum Anschalten'}
                    className={`flex items-center gap-1 rounded-sm border px-1.5 py-0.5 transition-colors ${l.include_headline
                      ? 'border-sky-500/40 bg-sky-500/10 text-sky-300'
                      : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    <Heading className="w-3 h-3" /> {l.include_headline ? 'Überschrift an' : 'ohne Überschrift'}
                  </button>
                  {l.letter_kind !== 'breakup' && (l.landing
                    ? <span className="text-sky-400 flex items-center gap-1"><QrCode className="w-3 h-3" /> Flyer</span>
                    : <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> no flyer</span>)}
                  <Button size="sm" variant="outline" onClick={(e) => openPrintPreview(l, e)} disabled={busyId === l.id}
                    data-testid={`approval-rewrite-btn-${l.id}`}
                    title="Vorschau mit Brief/Flyer/Umschlag-Tabs — dort nachjustieren und einzelne Dokumente neu schreiben"
                    className="h-9 sm:h-7 text-xs sm:text-[11px] rounded-sm border-purple-500/40 text-purple-300 hover:text-purple-200 hover:bg-purple-500/10">
                    {busyId === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />} Vorschau · Nachschreiben
                  </Button>
                </span>
              </div>
            ))}
            {!sq && rewritable.length > 10 && (
              <p className="px-4 py-2 text-[10px] text-muted-foreground">…{rewritable.length - 10} weitere — über die Suche nach Unternehmen oder Unternehmer finden.</p>
            )}
          </div>
        )}
      </CollapsibleSection>

      {/* iAuto handwriting machine queue */}
      <PrintQueueCard refreshKey={printRefresh} onLettersChanged={load} />
      <QrReprintDialog open={qrDialogOpen} onOpenChange={setQrDialogOpen} letters={qrSelected} onRemove={removeQr} />
      <PrintPreviewDialog letter={previewLetter} onClose={() => setPreviewLetter(null)}
        onConfirm={confirmWrite} confirming={!!busyId} mailing={mailing?.status} onHeadlineChange={load}
        checks={checksMap} onCheckChanged={() => setPrintRefresh(n => n + 1)}
        onNextRecipient={goNextRecipient}
        progress={{
          index: Math.max(1, physicalApproved.findIndex(l => l.id === previewLetter?.id) + 1),
          total: physicalApproved.length, checked: checkedRecipients,
        }} />
    </div>
  );
}
